import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { Container } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Button,
} from '@vnetwork/ui/primitives';
import { Timeline } from '@vnetwork/ui/data';
import { ConfirmDialog } from '@vnetwork/ui/feedback';
import { InfoGrid } from '@/pages/account/components/InfoGrid';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import type {
  DepreciationEntry,
  Item,
  WarehouseActivityLog,
  WarehouseProcess,
} from '@/api/capacities';
import { useDeleteItem } from '@/api/capacities';
import { itemInfoItems, logsToTimeline } from './inventory-detail-mappers';
import { InventoryDetailHeader } from './inventory-detail-header';
import { LinkedComponentsCard } from './linked-components-card';
import { LinkedProcessesTab } from './linked-processes-tab';
import { DepreciationTab } from './depreciation-tab';

export interface InventoryDetailViewProps {
  /** Back-to-list route + label. */
  backTo: string;
  backLabel: string;
  /** Muted reference prefix beside the title (service_tag / ref). */
  reference?: string;
  /** Noun for the info-card title + fields ("Equipment" / "Component"). */
  noun: 'Equipment' | 'Component';
  item?: Item;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /** Child components installed in this equipment (Linked Components card). */
  linkedComponents?: Item[];
  /** Activity log rows for the record. */
  logs: WarehouseActivityLog[];
  logsLoading: boolean;
  logsError: boolean;
  onRetryLogs: () => void;
  /** Warehouse processes linked to this item (Linked Processes tab). */
  processes: WarehouseProcess[];
  processesLoading: boolean;
  processesError: boolean;
  onRetryProcesses: () => void;
  /** Depreciation history entries (Depreciation tab). May fail → EmptyState. */
  depreciation: DepreciationEntry[];
  depreciationLoading: boolean;
  depreciationError: boolean;
}

/**
 * Shared read-only detail view for an inventory record (equipment / component).
 * Identity header (name + reference + status pill) over Overview (info cards)
 * and Activity (Timeline fed by the entity logs) tabs. Loading / error handled
 * via the shared tab-state components. Used by both the equipment and component
 * detail pages — the only difference is the id source (service_tag vs ref) and
 * the hooks the caller supplies.
 */
export function InventoryDetailView({
  backTo,
  backLabel,
  reference,
  noun,
  item,
  isLoading,
  isError,
  onRetry,
  linkedComponents,
  logs,
  logsLoading,
  logsError,
  onRetryLogs,
  processes,
  processesLoading,
  processesError,
  onRetryProcesses,
  depreciation,
  depreciationLoading,
  depreciationError,
}: InventoryDetailViewProps) {
  const navigate = useNavigate();
  const deleteItem = useDeleteItem();
  const [deleteOpen, setDeleteOpen] = useState(false);

  if (isError) {
    return (
      <Container size="full">
        <TabError title={`Failed to load ${backLabel.toLowerCase()}`} onRetry={onRetry} />
      </Container>
    );
  }
  if (isLoading || !item) {
    return (
      <Container size="full">
        <TabLoading rows={5} />
      </Container>
    );
  }

  const timelineItems = logsToTimeline(logs);

  const handleDelete = () => {
    deleteItem.mutate(item.id, {
      onSuccess: () => navigate(backTo),
    });
  };

  return (
    <Container size="full">
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <InventoryDetailHeader
          backTo={backTo}
          backLabel={backLabel}
          reference={reference}
          item={item}
          actions={
            <Button
              variant="danger"
              size="sm"
              startIcon={<Trash2 size={15} />}
              onClick={() => setDeleteOpen(true)}
            >
              Delete
            </Button>
          }
        />
        <ConfirmDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          title={`Delete this ${noun.toLowerCase()}?`}
          description={`"${item.name}" will be permanently removed from the warehouse. This cannot be undone.`}
          destructive
          confirmLabel={`Delete ${noun}`}
          loading={deleteItem.isPending}
          onConfirm={handleDelete}
        />

        <Card flat>
          <CardHeader>
            <CardTitle>{noun} Information</CardTitle>
          </CardHeader>
          <CardBody>
            <InfoGrid items={itemInfoItems(item, noun)} />
          </CardBody>
        </Card>

        {linkedComponents ? <LinkedComponentsCard components={linkedComponents} /> : null}

        <Tabs defaultValue="processes">
          <TabsList>
            <TabsTrigger value="processes">Linked Processes</TabsTrigger>
            <TabsTrigger value="activity">Activity Logs</TabsTrigger>
            <TabsTrigger value="depreciation">Depreciation</TabsTrigger>
          </TabsList>

          <TabsContent value="processes">
            <LinkedProcessesTab
              processes={processes}
              isLoading={processesLoading}
              isError={processesError}
              onRetry={onRetryProcesses}
            />
          </TabsContent>

          <TabsContent value="activity">
            {logsError ? (
              <TabError title="Failed to load activity" onRetry={onRetryLogs} />
            ) : logsLoading ? (
              <TabLoading rows={4} />
            ) : (
              <Card flat>
                <CardBody>
                  <Timeline items={timelineItems} emptyText="No activity recorded yet." />
                </CardBody>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="depreciation">
            <DepreciationTab
              itemId={item.id}
              item={item}
              history={depreciation}
              isLoading={depreciationLoading}
              isError={depreciationError}
            />
          </TabsContent>
        </Tabs>
      </div>
    </Container>
  );
}
